import { parseZone, utc } from 'moment';

function months(): number[] {
  return Array.from({ length: 12 }, (cur, i) => i + 1);
}

function years(amount: number): number[] {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: amount }, (cur, i) => currentYear + i);
}

function isCreditCardExpired(expMonth: string, expYear: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(`${expMonth}/1/${expYear}`) < today;
}

function day(date: Date): string {
  return parseZone(date).format('DD');
}

function weekDayName(date: Date, short = false): string {
  return parseZone(date).format(short ? 'ddd' : 'dddd');
}

function weekAndMonthDay(date: Date, short = true): string {
  return parseZone(date).format(short ? 'ddd, MMM D' : 'dddd, MMMM DD');
}

function monthDay(date: Date, short = false, doFormat = false): string {
  if (doFormat) {
    return parseZone(date).format('MMMM Do');
  }
  return parseZone(date).format(short ? 'MMM D' : 'MMMM D');
}

function month(date: Date, short = false): string {
  return parseZone(date).format(short ? 'MMM' : 'MMMM');
}

function daysElapsed(date: Date): number {
  return parseZone().diff(new Date(date), 'days');
}

function monthAndYear(date: Date): string {
  return parseZone(date).format('MMMM YYYY');
}

function monthAndDay(date: Date | undefined): string {
  return parseZone(date).format('MM/DD');
}

function monthDayAndYear(date: Date): string {
  return parseZone(date).format('MM/DD/YYYY');
}

function monthStart(date: Date): string {
  return parseZone(date).startOf('month').format('YYYY-MM-DD');
}

function monthEnd(date: Date): string {
  return parseZone(date).endOf('month').format('YYYY-MM-DD');
}

function addDays(date: Date, days: number): Date {
  const newDate = parseZone(date);
  newDate.add(days, 'days');
  return newDate.toDate();
}

function addMonths(date: Date, monthCount: number): Date {
  const newDate = parseZone(date);
  newDate.add(monthCount, 'months');
  /*  Previous implementation was returning a date without the timezone offset which
   resulted in the month being off by one day. The utc(date).format('MM-DD-YYYY') call
   preserves the proper date in the new Date object.
   Implemented in https://whitespectre.atlassian.net/browse/TPC-10681 */
  return new Date(utc(newDate).format('MM/DD/YYYY'));
}

function formatDateToShort(dateString: string): string {
  if (dateString) {
    const [_, rawMonth, rawDay] = dateString.split('-');
    return `${parseInt(rawMonth, 10)}/${parseInt(rawDay, 10)}`;
  }
  // @ts-expect-error TODO: fix null safety
  return null;
}

export {
  day,
  weekDayName,
  months,
  years,
  isCreditCardExpired,
  weekAndMonthDay,
  monthDay,
  month,
  daysElapsed,
  monthAndYear,
  monthAndDay,
  monthDayAndYear,
  monthStart,
  monthEnd,
  addMonths,
  addDays,
  formatDateToShort,
};
